import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SupportProfile } from '@/types';
import { storage } from '@/utils/storage';
import { STORAGE_KEYS } from '@/lib/config';
import { queryClient } from '@/lib/queryClient';

export type UserRole = 'superadmin' | 'tenantadmin' | 'support' | 'provideradmin' | 'providerstaff' | null;

export interface TenantPresentationMeta {
  currency?: string | null;
  timezone?: string | null;
  phonePrefix?: string | null;
  country?: string | null;
  countryCode?: string | null;
  timeFormat?: '12h' | '24h' | null;
}

export type AuthUser = Omit<Partial<SupportProfile>, 'id' | 'userId'> & {
  id: string | number;
  userId: string;
  email?: string;
  tenantId?: number | null;
};

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  role: UserRole;
  tenantId: number | null;
  providerId: number | null;
  /** Phase 3.Z — drives the email-verify banner. Null when claim absent. */
  email: string | null;
  emailConfirmed: boolean | null;
  impersonatedTenantId: number | null;
  impersonatedTenantName: string | null;
  tenantCurrency: string | null;
  tenantTimezone: string | null;
  tenantPhonePrefix: string | null;
  tenantCountry: string | null;
  tenantCountryCode: string | null;
  tenantTimeFormat: '12h' | '24h';
  tenantMetaLoaded: boolean;
  setUser: (user: AuthUser | null) => void;
  setIsLoading: (loading: boolean) => void;
  login: (token: string, refreshToken: string, user: AuthUser) => void;
  logout: () => void;
  setImpersonatedTenant: (tenantId: number | null, tenantName?: string | null) => void;
  setTenantMeta: (meta: TenantPresentationMeta) => void;
  /** Called after a successful /verify-email POST so the banner hides
   *  without requiring a fresh login + re-issued JWT. */
  markEmailConfirmed: () => void;
}

interface ParsedTokenClaims {
  isValid: boolean;
  role: UserRole;
  tenantId: number | null;
  providerId: number | null;
  email: string | null;
  emailConfirmed: boolean | null;
}

function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3 || !parts[1]) throw new Error('Malformed JWT');

  const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
  const payload = JSON.parse(new TextDecoder().decode(bytes));
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    throw new Error('Invalid JWT payload');
  }
  return payload as Record<string, unknown>;
}

function readPositiveId(value: unknown): number | null {
  const parsed = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function claimIsTrue(value: unknown): boolean {
  return value === true || (typeof value === 'string' && value.toLowerCase() === 'true');
}

function parseRoleFromToken(token: string): ParsedTokenClaims {
  try {
    const payload = decodeJwtPayload(token);
    const expiry = typeof payload.exp === 'number'
      ? payload.exp
      : Number.parseInt(String(payload.exp ?? ''), 10);
    if (Number.isFinite(expiry) && expiry * 1000 <= Date.now()) {
      throw new Error('Expired JWT');
    }

    const tenantId = readPositiveId(payload.TenantId);
    const providerId = readPositiveId(payload.ProviderId);
    const email = typeof payload.Email === 'string' ? payload.Email : null;
    const emailConfirmed = claimIsTrue(payload.EmailConfirmed)
      ? true
      : payload.EmailConfirmed === false || (typeof payload.EmailConfirmed === 'string' && payload.EmailConfirmed.toLowerCase() === 'false')
        ? false
        : null;
    if (claimIsTrue(payload.IsImpersonating) && claimIsTrue(payload.IsTenantAdmin)) {
      return { isValid: true, role: 'tenantadmin', tenantId, providerId: null, email, emailConfirmed };
    }
    if (claimIsTrue(payload.IsSuperAdmin)) return { isValid: true, role: 'superadmin', tenantId, providerId: null, email, emailConfirmed };
    if (claimIsTrue(payload.IsTenantAdmin)) return { isValid: true, role: 'tenantadmin', tenantId, providerId: null, email, emailConfirmed };
    if (claimIsTrue(payload.IsProviderAdmin)) return { isValid: true, role: 'provideradmin', tenantId, providerId, email, emailConfirmed };
    if (claimIsTrue(payload.IsSupport)) return { isValid: true, role: 'support', tenantId, providerId: null, email, emailConfirmed };
    if (claimIsTrue(payload.IsProvider) && tenantId != null) return { isValid: true, role: 'providerstaff', tenantId, providerId, email, emailConfirmed };
    return { isValid: false, role: null, tenantId, providerId: null, email, emailConfirmed };
  } catch {
    return { isValid: false, role: null, tenantId: null, providerId: null, email: null, emailConfirmed: null };
  }
}

/** Identity OTP tokens use jti for the authenticated account id. */
export function getDashboardTokenUserId(token: string): string | null {
  try { const id = decodeJwtPayload(token).jti; return typeof id === 'string' && id ? id : null; } catch { return null; }
}

export function isDashboardTokenUsable(token: string | null): boolean {
  return !!token && parseRoleFromToken(token).isValid;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      role: null,
      tenantId: null,
      providerId: null,
      email: null,
      emailConfirmed: null,
      impersonatedTenantId: null,
      impersonatedTenantName: null,
      tenantCurrency: null,
      tenantTimezone: null,
      tenantPhonePrefix: null,
      tenantCountry: null,
      tenantCountryCode: null,
      tenantTimeFormat: '12h',
      tenantMetaLoaded: false,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setIsLoading: (isLoading) => set({ isLoading }),

      login: (token, refreshToken, user) => {
        const { isValid, role, tenantId, providerId, email, emailConfirmed } = parseRoleFromToken(token);
        if (!isValid) {
          storage.clearTokens();
          set({
            user: null, isAuthenticated: false, isLoading: false, role: null,
            tenantId: null, providerId: null, email: null, emailConfirmed: null,
            impersonatedTenantId: null, impersonatedTenantName: null,
            tenantCurrency: null, tenantTimezone: null, tenantPhonePrefix: null,
            tenantCountry: null, tenantCountryCode: null, tenantTimeFormat: '12h', tenantMetaLoaded: false,
          });
          return;
        }
        const previous = get();
        if (previous.user?.userId !== user.userId || previous.role !== role || previous.tenantId !== tenantId || previous.providerId !== providerId) {
          queryClient.clear();
          set({ impersonatedTenantId: null, impersonatedTenantName: null });
        }
        storage.setAccessToken(token);
        storage.setRefreshToken(refreshToken);
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          role,
          tenantId,
          providerId,
          email,
          emailConfirmed,
          tenantCurrency: null,
          tenantTimezone: null,
          tenantPhonePrefix: null,
          tenantCountry: null,
          tenantCountryCode: null,
          tenantTimeFormat: '12h',
          tenantMetaLoaded: false,
        });
      },

      logout: () => {
        storage.clearTokens();
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          role: null,
          tenantId: null,
          providerId: null,
          email: null,
          emailConfirmed: null,
          impersonatedTenantId: null,
          impersonatedTenantName: null,
          tenantCurrency: null,
          tenantTimezone: null,
          tenantPhonePrefix: null,
          tenantCountry: null,
          tenantCountryCode: null,
          tenantTimeFormat: '12h',
          tenantMetaLoaded: false,
        });
      },

      setImpersonatedTenant: (tenantId, tenantName = null) => {
        // Switching the active tenant changes the scope of every server query
        // (apiClient sends X-Impersonate-Tenant). Clear cached data so all
        // tenant-scoped queries refetch under the new scope. Applies to both
        // entry points: the header TenantSwitcher and the tenant-details button.
        if (get().impersonatedTenantId !== tenantId) {
          queryClient.clear();
        }
        set({
          impersonatedTenantId: tenantId,
          impersonatedTenantName: tenantId === null ? null : tenantName,
          // Tenant-local formatting data must be refetched after every scope
          // change; retaining it leaks the previous tenant's currency/timezone.
          tenantCurrency: null,
          tenantTimezone: null,
          tenantPhonePrefix: null,
          tenantCountry: null,
          tenantCountryCode: null,
          tenantTimeFormat: '12h',
          tenantMetaLoaded: false,
        });
      },

      setTenantMeta: (meta) => set({
        tenantCurrency: meta.currency || null,
        tenantTimezone: meta.timezone || null,
        tenantPhonePrefix: meta.phonePrefix || null,
        tenantCountry: meta.country || null,
        tenantCountryCode: meta.countryCode?.toUpperCase() || null,
        tenantTimeFormat: meta.timeFormat === '24h' ? '24h' : '12h',
        tenantMetaLoaded: true,
      }),

      markEmailConfirmed: () => set({ emailConfirmed: true }),
    }),
    {
      name: STORAGE_KEYS.USER,
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        role: state.role,
        tenantId: state.tenantId,
        providerId: state.providerId,
        email: state.email,
        emailConfirmed: state.emailConfirmed,
        impersonatedTenantId: state.impersonatedTenantId,
        impersonatedTenantName: state.impersonatedTenantName,
        tenantCurrency: state.tenantCurrency,
        tenantTimezone: state.tenantTimezone,
        tenantPhonePrefix: state.tenantPhonePrefix,
        tenantCountry: state.tenantCountry,
        tenantCountryCode: state.tenantCountryCode,
        tenantTimeFormat: state.tenantTimeFormat,
        tenantMetaLoaded: state.tenantMetaLoaded,
      }),
    }
  )
);
